import { useCallback, useMemo } from 'react';

import { CircleCheckIcon, CircleIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router';

import PlainFieldset from '@/components/atoms/Fieldset/templates/PlainFieldset';
import Pie from '@/components/organisms/Analytics/Pie/Pie';
import { MODAL_MEMBER_INVITE } from '@/components/organisms/Modals/ModalConductor';
import useBrains from '@/hooks/useBrains';
import useCollections from '@/hooks/useCollections';
import { useDeskId } from '@/hooks/useDeskId';
import useHomeCheckList, { AccountUserPrefsEnum } from '@/hooks/useHomeCheckList';
import { useIntegrations } from '@/hooks/useIntegrations';
import { useRules } from '@/modules/rules/hooks/useRules';
import { pushModal } from '@/redux/modals/actions';
import { selectAccountSlug } from '@/redux/session/selectors';
import { isKeyEnter, preventClickThrough } from '@/util/util';

import { FIELDSET_CARD_PADDING } from '../../constants';

const HomeFieldset = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const slug = useSelector(selectAccountSlug);
  const { brains } = useBrains();
  const deskId = useDeskId();
  const { integrations } = useIntegrations(deskId);
  const { rules } = useRules(deskId);

  const brainId = brains?.[0]?.brain_id;
  const integrationId = useMemo(
    () =>
      integrations?.find((i) => i.type === 'web')?.integration_id ??
      integrations?.[0]?.integration_id,
    [integrations]
  );
  const { collections } = useCollections();

  const ruleId = rules.rules?.[0]?.rule_id ?? '';
  const firstCollectionId = collections?.[0]?.collection_id;

  const { getSteps, accountUserChecklist } = useHomeCheckList();

  const tiles = useMemo(
    () =>
      getSteps(
        accountUserChecklist,
        slug,
        brainId,
        deskId,
        integrationId,
        ruleId,
        firstCollectionId
      ),
    [
      getSteps,
      accountUserChecklist,
      slug,
      brainId,
      deskId,
      integrationId,
      ruleId,
      firstCollectionId,
    ]
  );

  const handleClick = useCallback(
    (index: number, stepIndex: number) => {
      const step = tiles[index]?.steps?.[stepIndex];
      if (!step) {
        return;
      } // Safety check for undefined step

      // Special handling for invite teammate - open modal instead of navigate
      if (step?.checklistStep === AccountUserPrefsEnum.ADD_TEAM_MEMBERS) {
        dispatch(pushModal(MODAL_MEMBER_INVITE));
        return;
      }
    },
    [tiles, dispatch]
  );

  const handleKeyUp = useCallback(
    (e: React.KeyboardEvent, index: number, stepIndex: number) => {
      preventClickThrough(e);
      if (isKeyEnter(e)) {
        handleClick(index, stepIndex);
      }
    },
    [handleClick]
  );

  const { total, completed } = useMemo(
    () =>
      tiles.reduce(
        (acc, tile) => ({
          total: acc.total + tile.steps.length,
          completed: acc.completed + tile.steps.filter((s) => s.completed).length,
        }),
        { total: 0, completed: 0 }
      ),
    [tiles]
  );

  const pieData = [
    { id: 'total', value: total - completed },
    { id: 'completed', value: completed },
  ];

  const piePercentage = total > 0 ? (completed / total) * 100 : 0;

  return (
    <PlainFieldset fullWidth className={FIELDSET_CARD_PADDING}>
      {/* Header Section */}
      <div className="mb-2 select-none">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex flex-col">
            <span className="mb-2 text-xl leading-7 font-semibold">{t('home.title')}</span>
            <span className="text-sm leading-5 font-normal text-foreground-muted">
              {t('home.subtitle')}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <div className="h-20 w-20 shrink-0">
              <Pie
                hasCenterMetric={false}
                enableArcLinkLabels={false}
                enableArcLabels={false}
                data={pieData}
                colors={['var(--color-stroke)', 'var(--color-accent)']}
              />
            </div>
            <div className="flex flex-col">
              <span className="text-xs leading-5 font-normal text-foreground-muted">
                {t('home.progress')}
              </span>
              <span className="text-right text-xl leading-7 font-bold">
                {Math.ceil(piePercentage)}%
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Unified List */}
      <div className="space-y-5 pb-6">
        {tiles.map(({ title, steps, Icon }, index) => {
          const isCompleted = steps.every((s) => s.completed);

          return (
            <div key={title}>
              {/* Section Header */}
              <div className="mb-2 flex items-center gap-2.5 select-none">
                <Icon size={18} className="text-foreground-muted" />
                <span className="text-base leading-6 font-semibold text-foreground">
                  {t(title)}
                </span>
                {isCompleted && <CircleCheckIcon className="ml-auto text-green-600" size={18} />}
              </div>

              {/* Section Items */}
              <ul className="ml-7 space-y-1">
                {steps.map(({ label, completed, url, checklistStep }, stepIndex) => {
                  const isClickable = Boolean(url);
                  return isClickable ? (
                    <li
                      key={label}
                      className="group flex w-fit items-center gap-2.5 py-0.5 text-sm"
                    >
                      <Link
                        to={
                          checklistStep !== AccountUserPrefsEnum.ADD_TEAM_MEMBERS ? url : undefined
                        }
                        className="flex w-fit cursor-pointer items-center gap-2.5 text-left hover:text-foreground"
                        onKeyUp={(e) => handleKeyUp(e, index, stepIndex)}
                        onClick={() => handleClick(index, stepIndex)}
                      >
                        {completed ? (
                          <CircleCheckIcon className="text-green-600" size={16} />
                        ) : (
                          <CircleIcon size={16} className="text-foreground-muted" />
                        )}
                        <span className="text-foreground-muted group-hover:text-foreground group-hover:underline">
                          {t(label)}
                        </span>
                      </Link>
                    </li>
                  ) : (
                    <li key={label} className="group flex items-center gap-2.5 py-0.5 text-sm">
                      {completed ? (
                        <CircleCheckIcon className="text-green-600" size={16} />
                      ) : (
                        <CircleIcon size={16} className="text-foreground-muted" />
                      )}
                      <span className="text-foreground-muted">{t(label)}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </div>
    </PlainFieldset>
  );
};

export default HomeFieldset;
